'use client';
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import { LoanComparison } from '@/lib/types';

interface Props {
  loans: LoanComparison[];
}

const shortProduct = (name: string): string => {
  const map: Record<string, string> = {
    'Crédito Habitação': 'C. Habitação',
    'Crédito Pessoal': 'C. Pessoal',
    'Crédito Automóvel': 'C. Auto',
    'Leasing Imobiliário': 'Leasing Im.',
    'Leasing Automóvel': 'Leasing Au.',
    'Crédito Empresas': 'C. Empresas',
  };
  return map[name] ?? name.slice(0, 12);
};

interface TooltipEntry {
  name: string;
  value: number;
  fill: string;
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipEntry[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg px-3 py-2.5 text-sm min-w-[200px]">
      <p className="font-semibold text-foreground mb-2 text-xs">{label}</p>
      {payload.map((p, i) => (
        <div key={`loan-chart-tip-${i}`} className="flex items-center justify-between gap-4 mb-1">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: p.fill }} />
            {p.name}
          </span>
          <span className="font-medium tabular-nums text-xs text-foreground">
            € {p.value.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function LoanBalanceChart({ loans }: Props) {
  const chartData = loans
    .filter((l) => l.saldoAnterior !== null || l.saldoAtual !== null)
    .map((l, i) => ({
      name: shortProduct(l.produtoFinanceiro),
      fullName: l.produtoFinanceiro,
      anterior: l.saldoAnterior ?? 0,
      atual: l.saldoAtual ?? 0,
      idx: i,
    }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }} barSize={18} barGap={4}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
          width={44}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.4 }} />
        <Legend
          wrapperStyle={{ fontSize: '11px', color: 'var(--muted-foreground)', paddingTop: '8px' }}
        />
        <Bar dataKey="anterior" name="Saldo Anterior" fill="var(--muted-foreground)" radius={[3, 3, 0, 0]} fillOpacity={0.5} />
        <Bar dataKey="atual" name="Saldo Actual" fill="var(--primary)" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}